import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import LiveBotChart from '../../components/LiveBotChart/LiveBotChart';
import { useTradingStore } from '../../stores/tradingStore';
import { useTradingWebSocket } from '../../hooks/useTradingWebSocket';
import { usePriceStreamWebSocket } from '../../hooks/usePriceStreamWebSocket';
import { useCandleUpdater } from '../../hooks/useCandleUpdater';
import ConfigPanel from './components/ConfigPanel/ConfigPanel';
import BotList from './components/BotList/BotList';
import type {
  Bot,
  BrokerConnection,
  Instrument,
  GridBotStatus,
  LiveTrade,
  Candle,
  PricePoint,
  GridLevels,
  TimeFrame,
  InstrumentsCache,
  TradingStatus,
} from '../../types/trading';
import './LiveTrading.css';

const LiveTrading: React.FC = () => {
  const navigate = useNavigate();

  // Persisted form state
  const {
    selectedBroker, setSelectedBroker,
    selectedFuture, setSelectedFuture,
    setSearchQuery,
    timeframe, setTimeframe,
    strategyId,
    capital,
    stopLoss,
    takeProfit,
    gridSize,
    priceRange,
    orderQuantity,
    updateInterval,
    minProfit,
  } = useTradingStore();

  // Runtime state
  const [availableBrokers, setAvailableBrokers] = useState<BrokerConnection[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [connectionKey, setConnectionKey] = useState(''); // Selected broker for trading
  const [dataConnectionKey, setDataConnectionKey] = useState(''); // Production broker for prices
  const [isStarting, setIsStarting] = useState(false);
  const [runningStrategies, setRunningStrategies] = useState<Bot[]>([]);
  const [error, setError] = useState('');
  const [, setWsConnected] = useState(false);
  const [gridBotStatuses, setGridBotStatuses] = useState<Record<string, GridBotStatus>>({});
  const [realtimePrices, setRealtimePrices] = useState<Record<string, number>>({});
  const [liveTrades, setLiveTrades] = useState<Record<string, LiveTrade[]>>({});
  const [priceHistory, setPriceHistory] = useState<Record<string, PricePoint[]>>({});

  // Chart state
  const [candleHistory, setCandleHistory] = useState<Record<string, Candle[]>>({});
  const [isLoadingCandles, setIsLoadingCandles] = useState(false);
  const [isLoadingMoreCandles, setIsLoadingMoreCandles] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const hasHistoricalDataRef = useRef<Record<string, boolean>>({});
  const currentCandleRef = useRef<Record<string, Candle>>({});
  const isLoadingMoreRef = useRef(false);

  // Bot selection state (for chart sync)
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [previewGridLevels, setPreviewGridLevels] = useState<GridLevels | null>(null);
  const [tradingStatus, setTradingStatus] = useState<TradingStatus>('unknown');

  // Real-time candle state (for efficient chart updates)
  const [realtimeCandle, setRealtimeCandle] = useState<Candle | null>(null);

  // Sync state
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const isReconnectingRef = useRef(false);
  const maxRetries = 3;

  // Modal state
  const [showAddBrokerModal, setShowAddBrokerModal] = useState(false);
  const [newBrokerToken, setNewBrokerToken] = useState('');
  const [newBrokerSandbox, setNewBrokerSandbox] = useState(true);
  const [isAddingBroker, setIsAddingBroker] = useState(false);

  // Cache functions
  const getCacheKey = (brokerKey: string) => `futures_cache_${brokerKey}`;

  const loadCacheForBroker = (brokerKey: string): InstrumentsCache | null => {
    try {
      const cached = localStorage.getItem(getCacheKey(brokerKey));
      if (cached) {
        const cache: InstrumentsCache = JSON.parse(cached);
        if (Date.now() - cache.timestamp < 3600000) return cache;
      }
    } catch (err) {
      console.error('Failed to load cache:', err);
    }
    return null;
  };

  const saveCacheForBroker = (brokerKey: string, instrumentsList: Instrument[], broker: string) => {
    // Limit cached instruments to prevent quota issues (top 500 by relevance)
    const limitedInstruments = instrumentsList.slice(0, 500);

    const saveToStorage = () => {
      const cache: InstrumentsCache = {
        data: limitedInstruments,
        timestamp: Date.now(),
        broker,
        connectionKey: brokerKey,
      };
      localStorage.setItem(getCacheKey(brokerKey), JSON.stringify(cache));
      setLastUpdate(Date.now());
    };

    try {
      saveToStorage();
    } catch (err) {
      // On quota exceeded, clear old caches and retry
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing old caches...');
        // Clear all futures_cache entries
        Object.keys(localStorage)
          .filter(key => key.startsWith('futures_cache_'))
          .forEach(key => localStorage.removeItem(key));
        try {
          saveToStorage();
        } catch (retryErr) {
          console.error('Failed to save cache after clearing:', retryErr);
        }
      } else {
        console.error('Failed to save cache:', err);
      }
    }
  };

  // Timeframe utils
  const getApiInterval = (tf: TimeFrame): string => {
    const mapping: Record<TimeFrame, string> = {
      '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1h', '4h': '4h', '1D': '1d',
    };
    return mapping[tf];
  };

  const getTimeframeDuration = (tf: TimeFrame): number => {
    const mapping: Record<TimeFrame, number> = {
      '1m': 60000, '5m': 300000, '15m': 900000, '1h': 3600000, '4h': 14400000, '1D': 86400000,
    };
    return mapping[tf];
  };

  // Fetch trades for a specific bot from broker
  const fetchBotTrades = useCallback(async (botId: string) => {
    try {
      const response = await fetch(getApiUrl(`/strategies/${botId}/trades`), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.trades?.length > 0) {
          const trades: LiveTrade[] = data.trades.map((t: any) => ({
            id: t.id,
            type: t.side as 'buy' | 'sell',
            price: t.price,
            quantity: t.quantity,
            time: new Date(t.timestamp).getTime() / 1000,
            pnl: t.pnl,
          }));
          setLiveTrades(prev => ({
            ...prev,
            [botId]: trades,
          }));
        }
      }
    } catch (err) {
      console.error(`Failed to fetch trades for bot ${botId}:`, err);
    }
  }, []);

  // Fetch running strategies
  const fetchRunningStrategies = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/strategies/running'), { credentials: 'include' });
      const data = await response.json();
      if (data.strategies) {
        setRunningStrategies(data.strategies);
        // Fetch trades for each running strategy
        for (const strategy of data.strategies) {
          if (strategy.status === 'running') {
            fetchBotTrades(strategy.id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch running strategies:', err);
    }
  }, [fetchBotTrades]);

  // WebSocket hooks
  useTradingWebSocket({
    onConnected: () => setWsConnected(true),
    onDisconnected: () => setWsConnected(false),
    onStrategiesUpdate: (strategies) => {
      setRunningStrategies(strategies);
      const activeIds = new Set(strategies.map(s => s.id));
      setGridBotStatuses(prev => {
        const next: Record<string, GridBotStatus> = {};
        for (const key of Object.keys(prev)) {
          if (activeIds.has(key)) next[key] = prev[key];
        }
        return next;
      });
    },
    onGridStatusUpdate: (runId, status) => {
      setGridBotStatuses(prev => ({ ...prev, [runId]: status }));
      if (status.total_pnl !== undefined) {
        setRunningStrategies(prev =>
          prev.map(s => s.id === runId ? { ...s, pnl: status.total_pnl } : s)
        );
      }
    },
    onPriceUpdate: (symbol, price) => {
      setRealtimePrices(prev => ({ ...prev, [symbol]: price }));
    },
    onTradeExecuted: (runId, trade) => {
      setLiveTrades(prev => ({
        ...prev,
        [runId]: [...(prev[runId] || []), trade],
      }));
      fetchRunningStrategies();
    },
    onBotEvent: (type, runId) => {
      if (type === 'bot_stopped' || type === 'stop_loss' || type === 'take_profit') {
        setGridBotStatuses(prev => {
          const { [runId]: _, ...rest } = prev;
          return rest;
        });
      }
      fetchRunningStrategies();
    },
  });

  // Stable callback for candle updates (memoized to prevent useCandleUpdater recreation)
  const handleCandleUpdate = useCallback((_symbol: string, candle: Candle, _isNew: boolean) => {
    // Update real-time candle for efficient chart update via update()
    // Don't trigger setCandleHistory here to avoid setData/update conflicts
    setRealtimeCandle(candle);
  }, []);

  // Candle updater with proper time bucket detection
  const { updateWithPrice, setCandles: setCandleUpdaterCandles } = useCandleUpdater({
    timeframe,
    onCandleUpdate: handleCandleUpdate,
  });

  // Sync candle updater when historical data loads
  useEffect(() => {
    if (selectedFuture && candleHistory[selectedFuture]?.length > 0) {
      setCandleUpdaterCandles(selectedFuture, candleHistory[selectedFuture]);
    }
  }, [selectedFuture, candleHistory, setCandleUpdaterCandles]);

  // Fill gaps after WebSocket reconnect (fetch only missing candles)
  const fillCandleGaps = useCallback(async () => {
    if (!selectedFuture) return;
    const existing = candleHistory[selectedFuture];
    if (!existing || existing.length === 0) return;

    const lastCandleTime = existing[existing.length - 1].time;
    const now = new Date();
    const from = new Date(lastCandleTime * 1000);

    // Only fetch if gap is significant (more than one candle period)
    const tfDuration = getTimeframeDuration(timeframe);
    if (now.getTime() - from.getTime() < tfDuration * 1.5) return;

    console.log('[Candles] Filling gap from', from.toISOString(), 'to', now.toISOString());

    const broker = availableBrokers.find(b => b.connection_key === selectedBroker);
    const candleConnectionKey = dataConnectionKey || connectionKey;

    try {
      const params = new URLSearchParams({
        broker: broker?.broker || 'TBANK',
        symbol: selectedFuture,
        interval: getApiInterval(timeframe),
        from: from.toISOString(),
        to: now.toISOString(),
      });
      if (candleConnectionKey) {
        params.set('connection_key', candleConnectionKey);
      }

      const response = await fetch(`${getApiUrl('/market-data/candles')}?${params}`, { credentials: 'include' });
      if (!response.ok) return;

      const data = await response.json();
      if (data.candles?.length > 0) {
        const newCandles = data.candles.map((c: any) => ({
          time: Math.floor(new Date(c.Time).getTime() / 1000),
          open: c.Open,
          high: c.High,
          low: c.Low,
          close: c.Close,
          volume: c.Volume,
        }));

        setCandleHistory(prev => {
          const existingCandles = prev[selectedFuture] || [];
          const byTime = new Map(existingCandles.map(c => [c.time, c]));
          for (const c of newCandles) byTime.set(c.time, c);
          return { ...prev, [selectedFuture]: Array.from(byTime.values()).sort((a, b) => a.time - b.time) };
        });

        console.log('[Candles] Filled', newCandles.length, 'candles');
      }
    } catch (err) {
      console.error('[Candles] Gap fill failed:', err);
    }
  }, [selectedFuture, candleHistory, timeframe, availableBrokers, selectedBroker, connectionKey, dataConnectionKey]);

  // Use production account for price streaming (dataConnectionKey), fallback to selected
  usePriceStreamWebSocket({
    connectionKey: dataConnectionKey || connectionKey,
    symbol: selectedFuture,
    onPriceUpdate: (symbol, price, timestamp) => {
      setRealtimePrices(prev => ({ ...prev, [symbol]: price }));
      // Use new candle updater with time bucket detection
      if (hasHistoricalDataRef.current[symbol]) {
        updateWithPrice(symbol, price, timestamp);
      }
    },
    onPriceHistory: (symbol, point) => {
      setPriceHistory(prev => {
        const arr = prev[symbol] || [];
        if (arr.length > 0 && arr[arr.length - 1].time === point.time) {
          const updated = [...arr];
          updated[updated.length - 1] = point;
          return { ...prev, [symbol]: updated };
        }
        return { ...prev, [symbol]: [...arr, point] };
      });
    },
    onReconnect: fillCandleGaps,
  });

  // Fetch historical candles
  const fetchHistoricalCandles = useCallback(async (symbol: string, tf: TimeFrame, broker: string = 'TBANK') => {
    if (!symbol) return;
    setIsLoadingCandles(true);
    setNoDataMessage(null);

    try {
      const now = new Date();
      const candleCount = 500;
      const tfDuration = getTimeframeDuration(tf);
      const minRange = 7 * 24 * 60 * 60 * 1000;
      const actualRange = Math.max(candleCount * tfDuration, minRange);
      const from = new Date(now.getTime() - actualRange);

      const params = new URLSearchParams({
        broker,
        symbol,
        interval: getApiInterval(tf),
        from: from.toISOString(),
        to: now.toISOString(),
        limit: String(candleCount),
      });
      // Add connection_key for API fallback when DB is empty
      // Use data connection (production) for candles if available
      const candleConnectionKey = dataConnectionKey || connectionKey;
      if (candleConnectionKey) {
        params.set('connection_key', candleConnectionKey);
      }

      const response = await fetch(`${getApiUrl('/market-data/candles')}?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch candles');

      const data = await response.json();
      if (data.candles?.length > 0) {
        const candles: Candle[] = data.candles.map((c: any) => ({
          time: Math.floor(new Date(c.Time).getTime() / 1000),
          open: c.Open,
          high: c.High,
          low: c.Low,
          close: c.Close,
          volume: c.Volume,
        }));

        setCandleHistory(prev => ({ ...prev, [symbol]: candles }));
        hasHistoricalDataRef.current[symbol] = true;
        if (candles.length > 0) {
          currentCandleRef.current[symbol] = { ...candles[candles.length - 1] };
        }
      } else {
        setNoDataMessage(`Нет данных для ${symbol}. Загрузите историю.`);
        setCandleHistory(prev => ({ ...prev, [symbol]: [] }));
        hasHistoricalDataRef.current[symbol] = false;
      }
    } catch (err) {
      console.error('[Candles] Failed to fetch:', err);
    } finally {
      setIsLoadingCandles(false);
    }
  }, [connectionKey, dataConnectionKey]);

  // Lazy load more candles
  const loadMoreCandles = useCallback(async (beforeTimestamp: number) => {
    if (!selectedFuture || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMoreCandles(true);

    try {
      const candleCount = 200;
      const tfDuration = getTimeframeDuration(timeframe);
      const to = new Date(beforeTimestamp * 1000);
      const minRange = 7 * 24 * 60 * 60 * 1000;
      const from = new Date(to.getTime() - Math.max(candleCount * tfDuration, minRange));

      const broker = availableBrokers.find(b => b.connection_key === selectedBroker);

      const params = new URLSearchParams({
        broker: broker?.broker || 'TBANK',
        symbol: selectedFuture,
        interval: getApiInterval(timeframe),
        from: from.toISOString(),
        to: to.toISOString(),
        limit: String(candleCount),
      });
      // Use data connection (production) for candles
      const candleConnectionKey = dataConnectionKey || connectionKey;
      if (candleConnectionKey) {
        params.set('connection_key', candleConnectionKey);
      }

      const response = await fetch(`${getApiUrl('/market-data/candles')}?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch more candles');

      const data = await response.json();
      if (data.candles?.length > 0) {
        const newCandles: Candle[] = data.candles.map((c: any) => ({
          time: Math.floor(new Date(c.Time).getTime() / 1000),
          open: c.Open,
          high: c.High,
          low: c.Low,
          close: c.Close,
          volume: c.Volume,
        }));
        setCandleHistory(prev => {
          const existing = prev[selectedFuture] || [];
          const byTime = new Map<number, Candle>();
          for (const c of newCandles) byTime.set(c.time, c);
          for (const c of existing) byTime.set(c.time, c);
          return { ...prev, [selectedFuture]: Array.from(byTime.values()).sort((a, b) => a.time - b.time) };
        });
      }
    } catch (err) {
      console.error('[Candles] Failed to load more:', err);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMoreCandles(false);
    }
  }, [selectedFuture, timeframe, selectedBroker, availableBrokers, connectionKey, dataConnectionKey]);

  // Fetch candles on asset/timeframe change + periodic refresh to prevent gaps
  useEffect(() => {
    // Use production account for candle data if available
    const candleKey = dataConnectionKey || connectionKey;
    if (!selectedFuture || availableBrokers.length === 0 || !candleKey) return;

    const broker = availableBrokers.find(b => b.connection_key === selectedBroker);
    const brokerName = broker?.broker || 'TBANK';

    // Initial fetch only - real-time updates handled by WebSocket + useCandleUpdater
    fetchHistoricalCandles(selectedFuture, timeframe, brokerName);
  }, [selectedFuture, timeframe, selectedBroker, availableBrokers, connectionKey, dataConnectionKey, fetchHistoricalCandles]);

  // Fetch trading status for selected instrument (use production account for data)
  useEffect(() => {
    const statusConnectionKey = dataConnectionKey || connectionKey;
    if (!selectedFuture || !statusConnectionKey) {
      setTradingStatus('unknown');
      return;
    }

    const fetchTradingStatus = async () => {
      try {
        const response = await fetch(
          getApiUrl(`/market-data/trading-status?connection_key=${statusConnectionKey}&figi=${selectedFuture}`),
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setTradingStatus(data.trading_status || 'unknown');
        }
      } catch (err) {
        console.error('Failed to fetch trading status:', err);
        setTradingStatus('unknown');
      }
    };

    fetchTradingStatus();
    // Refresh status every 30 seconds
    const interval = setInterval(fetchTradingStatus, 30000);
    return () => clearInterval(interval);
  }, [selectedFuture, connectionKey, dataConnectionKey]);

  // Fetch running strategies on mount
  useEffect(() => {
    fetchRunningStrategies();
  }, [fetchRunningStrategies]);

  // Load broker connections on mount
  useEffect(() => {
    const loadConnections = async () => {
      try {
        const response = await fetch(getApiUrl('/broker-connections'), { credentials: 'include' });
        const data = await response.json();
        if (data.connections?.length > 0) {
          setAvailableBrokers(data.connections);

          // Find production account for data (prices always from real account)
          const productionAccount = data.connections.find((c: BrokerConnection) => !c.sandbox);
          if (productionAccount) {
            setDataConnectionKey(productionAccount.connection_key);
            // Reconnect production account for price streaming
            reconnectBroker(productionAccount, false, true); // isDataAccount=true
          }

          // Select trading account (prefer saved, then sandbox, then first available)
          const saved = selectedBroker ? data.connections.find((c: BrokerConnection) => c.connection_key === selectedBroker) : null;
          const sandboxAccount = data.connections.find((c: BrokerConnection) => c.sandbox);
          const tradingBroker = saved || sandboxAccount || data.connections[0];
          if (!saved) setSelectedBroker(tradingBroker.connection_key);
          reconnectBroker(tradingBroker);
        }
      } catch (err) {
        console.error('Failed to load connections:', err);
      }
    };
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconnect broker
  // isDataAccount=true means this is the production account used only for price data
  const reconnectBroker = async (broker: BrokerConnection, isRetry = false, isDataAccount = false) => {
    if (isReconnectingRef.current && !isDataAccount) return;
    if (!isDataAccount) isReconnectingRef.current = true;
    if (!isDataAccount) setError('');

    const cache = loadCacheForBroker(broker.connection_key);
    if (cache && !isRetry && !isDataAccount) {
      setInstruments(cache.data);
      setLastUpdate(cache.timestamp);
    }

    try {
      const response = await fetch(getApiUrl('/brokers/connect-secure'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ connection_key: broker.connection_key }),
      });
      const data = await response.json();
      if (response.ok) {
        const newKey = data.connection_key || broker.connection_key;
        if (isDataAccount) {
          // Only update dataConnectionKey for price streaming
          setDataConnectionKey(newKey);
        } else {
          // Update trading connection
          setConnectionKey(newKey);
          setSelectedBroker(newKey);
          setRetryAttempt(0);
          setTimeout(() => fetchInstruments(newKey, broker.broker), 2000);
        }
      } else {
        throw new Error(data.error || 'Failed to connect');
      }
    } catch (err: any) {
      if (!isDataAccount) {
        setError(err.message || 'Ошибка подключения');
        if (retryAttempt < maxRetries) {
          setRetryAttempt(prev => prev + 1);
          setTimeout(() => {
            isReconnectingRef.current = false;
            reconnectBroker(broker, true, isDataAccount);
          }, 2000 * Math.pow(2, retryAttempt));
          return;
        }
      }
    } finally {
      if (!isDataAccount) isReconnectingRef.current = false;
    }
  };

  const fetchInstruments = async (key: string, broker: string) => {
    try {
      const response = await fetch(getApiUrl(`/brokers/${key}/instruments`), { credentials: 'include' });
      const data = await response.json();
      if (data.instruments) {
        setInstruments(data.instruments);
        saveCacheForBroker(key, data.instruments, broker);
        setError('');
      }
    } catch (err) {
      setError('Не удалось загрузить инструменты');
    }
  };

  // Handlers
  const handleBrokerChange = (brokerKey: string) => {
    setSelectedBroker(brokerKey);
    setConnectionKey(brokerKey);
    setSelectedFuture('');
    setSearchQuery('');

    const cache = loadCacheForBroker(brokerKey);
    if (cache) {
      setInstruments(cache.data);
      setLastUpdate(cache.timestamp);
    } else {
      setInstruments([]);
      setLastUpdate(null);
    }

    const broker = availableBrokers.find(b => b.connection_key === brokerKey);
    if (broker) fetchInstruments(brokerKey, broker.broker);
  };

  const handleInstrumentSelect = (figi: string, displayName: string) => {
    setSelectedFuture(figi);
    setSelectedBotId(null); // Clear bot selection when manually selecting asset
  };

  const handleBotSelect = (botId: string, symbol: string) => {
    setSelectedBotId(botId);
    setSelectedFuture(symbol);
    setSearchQuery('');
    // Find instrument name
    const inst = instruments.find(i => i.figi === symbol);
    if (inst) setSearchQuery(`${inst.ticker} - ${inst.name}`);
    // Fetch trades for this bot from broker
    fetchBotTrades(botId);
  };

  const handleStartBot = async () => {
    if (!connectionKey) {
      setError('Подключите брокерский счёт');
      return;
    }
    setIsStarting(true);
    setError('');

    try {
      const broker = availableBrokers.find(b => b.connection_key === selectedBroker);
      const params: any = {
        strategy_id: strategyId,
        symbol: selectedFuture,
        broker_connection_key: connectionKey,
        capital: parseFloat(capital),
        stop_loss: parseFloat(stopLoss),
        take_profit: parseFloat(takeProfit),
        broker_name: broker?.broker || 'tbank',
        broker_account_id: broker?.account_id || '',
        broker_sandbox: broker?.sandbox ?? true,
      };

      if (strategyId === 'grid_bot') {
        params.strategy_config = {
          grid_size: parseInt(gridSize),
          price_range: parseFloat(priceRange),
          order_quantity: parseInt(orderQuantity),
          update_interval: parseInt(updateInterval),
          min_profit: parseFloat(minProfit),
          stop_loss: parseFloat(stopLoss),
          take_profit: parseFloat(takeProfit),
        };
      }

      const response = await fetch(getApiUrl('/strategies/start'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(params),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start bot');
      setPreviewGridLevels(null); // Clear preview when bot starts
      await fetchRunningStrategies();
    } catch (err: any) {
      setError(err.message || 'Failed to start bot');
    } finally {
      setIsStarting(false);
    }
  };

  const handleRunBacktest = () => {
    navigate('/backtest', {
      state: {
        future: selectedFuture,
        strategy: strategyId,
        capital: parseFloat(capital),
        stop_loss: parseFloat(stopLoss),
        take_profit: parseFloat(takeProfit),
        strategy_config: strategyId === 'grid_bot' ? {
          grid_size: parseInt(gridSize),
          price_range: parseFloat(priceRange),
          order_quantity: parseInt(orderQuantity),
          update_interval: parseInt(updateInterval),
          min_profit: parseFloat(minProfit),
        } : undefined,
      },
    });
  };

  const handleStopBot = async (botId: string) => {
    // Optimistic UI update - remove bot immediately
    setRunningStrategies(prev => prev.filter(s => s.id !== botId));
    setGridBotStatuses(prev => {
      const { [botId]: _, ...rest } = prev;
      return rest;
    });
    if (selectedBotId === botId) {
      setSelectedBotId(null);
    }

    try {
      await fetch(getApiUrl(`/strategies/${botId}/stop`), { method: 'POST', credentials: 'include' });
      // Confirm with server state
      await fetchRunningStrategies();
    } catch (err) {
      console.error('Failed to stop bot:', err);
      // Rollback on error - refetch actual state
      await fetchRunningStrategies();
    }
  };

  const handlePauseBot = async (botId: string) => {
    // Optimistic UI update
    setRunningStrategies(prev =>
      prev.map(s => s.id === botId ? { ...s, status: 'paused' } : s)
    );

    try {
      await fetch(getApiUrl(`/strategies/${botId}/pause`), { method: 'POST', credentials: 'include' });
      await fetchRunningStrategies();
    } catch (err) {
      console.error('Failed to pause bot:', err);
      await fetchRunningStrategies();
    }
  };

  const handleResumeBot = async (botId: string) => {
    // Optimistic UI update
    setRunningStrategies(prev =>
      prev.map(s => s.id === botId ? { ...s, status: 'running' } : s)
    );

    try {
      await fetch(getApiUrl(`/strategies/${botId}/resume`), { method: 'POST', credentials: 'include' });
      await fetchRunningStrategies();
    } catch (err) {
      console.error('Failed to resume bot:', err);
      await fetchRunningStrategies();
    }
  };

  const handleAddBroker = async () => {
    if (!newBrokerToken.trim()) {
      setError('Введите API токен');
      return;
    }
    setIsAddingBroker(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/broker-connections'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          broker: 'TBANK',
          api_token: newBrokerToken,
          sandbox: newBrokerSandbox,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        const newConnection: BrokerConnection = {
          broker: 'TBANK',
          connection_key: data.connection_key,
          account_id: '',
          sandbox: newBrokerSandbox,
          status: 'saved',
        };
        setAvailableBrokers(prev => [...prev, newConnection]);
        setShowAddBrokerModal(false);
        setNewBrokerToken('');
        reconnectBroker(newConnection);
      } else {
        setError(data.error || 'Не удалось сохранить');
      }
    } catch (err) {
      setError('Ошибка сохранения');
    } finally {
      setIsAddingBroker(false);
    }
  };

  // Compute chart data - MEMOIZED to prevent unnecessary re-renders
  // Prefer selectedBotId (when user clicks a bot), fallback to any running bot for the asset
  const activeBotForAsset = useMemo(() => {
    // If user explicitly selected a bot, use that one
    if (selectedBotId) {
      const selectedBot = runningStrategies.find(s => s.id === selectedBotId && s.status === 'running');
      if (selectedBot) return selectedBot;
    }
    // Otherwise find any running bot for the current asset
    return runningStrategies.find(s => s.symbol === selectedFuture && s.status === 'running');
  }, [runningStrategies, selectedFuture, selectedBotId]);

  // Fallback: fetch grid status via REST if not available via WebSocket
  useEffect(() => {
    if (!activeBotForAsset) return;
    if (gridBotStatuses[activeBotForAsset.id]) return; // Already have data

    const fetchGridStatus = async () => {
      try {
        const response = await fetch(getApiUrl(`/strategies/${activeBotForAsset.id}/status`), { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.grid_bot) {
            setGridBotStatuses(prev => ({ ...prev, [activeBotForAsset.id]: data.grid_bot as GridBotStatus }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch grid status:', err);
      }
    };

    fetchGridStatus();
  }, [activeBotForAsset, gridBotStatuses]);

  const activeGridLevels = useMemo(() => {
    if (!activeBotForAsset || !gridBotStatuses[activeBotForAsset.id]?.grid_range) return undefined;
    const status = gridBotStatuses[activeBotForAsset.id];
    const parts = status.grid_range.split(' - ');
    const low = parseFloat(parts[0]);
    const high = parseFloat(parts[1]);
    return !isNaN(low) && !isNaN(high) ? { low, high, count: status.grid_levels } : undefined;
  }, [activeBotForAsset, gridBotStatuses]);

  // CRITICAL: Memoize assetTrades to prevent setData() being called on every price tick
  // Show trades for selected bot, or all bots on the asset if none selected
  const assetTrades = useMemo(() => {
    let result: LiveTrade[];
    if (selectedBotId && liveTrades[selectedBotId]) {
      result = [...liveTrades[selectedBotId]].sort((a, b) => a.time - b.time);
    } else {
      result = runningStrategies
        .filter(s => s.symbol === selectedFuture)
        .flatMap(s => liveTrades[s.id] || [])
        .sort((a, b) => a.time - b.time);
    }
    console.log('[LiveTrading] assetTrades:', {
      count: result.length,
      selectedBotId,
      selectedFuture,
      liveTrades: Object.keys(liveTrades).map(id => ({ id, count: liveTrades[id]?.length || 0 })),
      trades: result,
    });
    return result;
  }, [runningStrategies, selectedFuture, liveTrades, selectedBotId]);

  const assetPriceData = useMemo(() => priceHistory[selectedFuture] || [], [priceHistory, selectedFuture]);
  const selectedInstrumentInfo = useMemo(() => instruments.find(inst => inst.figi === selectedFuture), [instruments, selectedFuture]);

  // Memoize candles to prevent || [] from creating new array on every render
  const chartCandles = useMemo(() => candleHistory[selectedFuture] || [], [candleHistory, selectedFuture]);

  // Empty state
  if (availableBrokers.length === 0) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="card">
            <h2>Подключите брокерский счёт</h2>
            <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
              Для запуска ботов необходимо подключить брокерский счёт
            </p>
            <button className="primary" onClick={() => navigate('/brokers')}>
              Перейти в "Мои биржи"
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trading-page">
      <div className="trading-layout">
        {/* Chart Area (left side) */}
        <div className="trading-chart">
          <LiveBotChart
            priceData={assetPriceData}
            candles={chartCandles}
            trades={assetTrades}
            gridLevels={activeGridLevels}
            previewGridLevels={previewGridLevels || undefined}
            height={500}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
            isLoading={isLoadingCandles}
            onLoadMore={loadMoreCandles}
            isLoadingMore={isLoadingMoreCandles}
            symbol={selectedFuture}
            symbolName={selectedInstrumentInfo?.ticker}
            tradingStatus={tradingStatus}
            noDataMessage={noDataMessage || undefined}
            realtimeCandle={realtimeCandle}
          />
        </div>

        {/* Config Panel (right side) */}
        <ConfigPanel
          availableBrokers={availableBrokers}
          instruments={instruments}
          currentPrice={realtimePrices[selectedFuture]}
          isStarting={isStarting}
          error={error}
          onBrokerChange={handleBrokerChange}
          onInstrumentSelect={handleInstrumentSelect}
          onStartBot={handleStartBot}
          onRunBacktest={handleRunBacktest}
          onAddBroker={() => setShowAddBrokerModal(true)}
          onPreviewGridChange={setPreviewGridLevels}
        />

        {/* Bot List (bottom) */}
        <BotList
          bots={runningStrategies}
          gridBotStatuses={gridBotStatuses}
          liveTrades={liveTrades}
          instruments={instruments}
          selectedBotId={selectedBotId}
          onBotSelect={handleBotSelect}
          onPauseBot={handlePauseBot}
          onResumeBot={handleResumeBot}
          onStopBot={handleStopBot}
        />
      </div>

      {/* Add Broker Modal */}
      {showAddBrokerModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '450px',
          }}>
            <h3 style={{ margin: '0 0 20px', color: 'white' }}>Добавить брокера</h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: '#9ca3af', fontSize: '14px' }}>
                API Токен
              </label>
              <input
                type="password"
                value={newBrokerToken}
                onChange={(e) => setNewBrokerToken(e.target.value)}
                placeholder="Введите токен..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newBrokerSandbox}
                  onChange={(e) => setNewBrokerSandbox(e.target.checked)}
                />
                Песочница
              </label>
            </div>

            {error && (
              <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(239,68,68,0.15)', borderRadius: '6px', color: '#ef4444', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowAddBrokerModal(false); setNewBrokerToken(''); setError(''); }}
                style={{ padding: '10px 20px', background: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Отмена
              </button>
              <button
                onClick={handleAddBroker}
                disabled={isAddingBroker || !newBrokerToken.trim()}
                style={{
                  padding: '10px 20px',
                  background: isAddingBroker ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isAddingBroker ? 'not-allowed' : 'pointer',
                }}
              >
                {isAddingBroker ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTrading;
